/**
 * faviconManager.js — Dynamic Cyberpunk Favicon Controller for Dard-e-Disco.
 * 
 * Features:
 * 1. Homepage Mode: Alternating 2-frame retro disco dancer (~440ms tempo).
 *    - Frame A (Static Pose): Left arm up-then-down, Right arm down-then-up (matching the hologram avatar).
 *    - Frame B: Reversed arm wave (Left down-then-up, Right up-then-down).
 *    - Frames are pre-rendered into cached data URLs at boot time: 0 Canvas redraws, 0 GC churn.
 *    - Automatically pauses when tab is hidden via Page Visibility API.
 * 2. Room Active Mode (Concept B):
 *    - Static "Target Locked" cyberpunk HUD icon with tracking brackets [ + ] and Frame A pose.
 *    - Zero intervals, zero CPU overhead during WebGL + MediaPipe execution.
 */

let timer = null;
let currentMode = null;
let linkEl = null;

// Cached data URLs
let frameAUrl = null;
let frameBUrl = null;
let roomActiveUrl = null;

function getFaviconLink() {
  if (linkEl && document.head.contains(linkEl)) return linkEl;
  linkEl = document.getElementById("dynamic-favicon");
  if (!linkEl) {
    linkEl = document.querySelector("link[rel*='icon']");
  }
  if (!linkEl) {
    linkEl = document.createElement("link");
    linkEl.id = "dynamic-favicon";
    linkEl.rel = "shortcut icon";
    document.head.appendChild(linkEl);
  }
  return linkEl;
}

/**
 * Pre-renders a 32x32 canvas frame into a crisp PNG data URL
 */
function renderFrameToDataUrl(drawFn) {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // Rounded dark badge background for guaranteed high-contrast in light/dark tabs
  ctx.fillStyle = "#0a0a10";
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(0, 0, 32, 32, 7);
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, 32, 32);
  }

  drawFn(ctx);
  return canvas.toDataURL("image/png");
}

function initCachedFrames() {
  if (frameAUrl && frameBUrl && roomActiveUrl) return;

  // --- Frame A (Static & Homepage Frame 1): Left up-then-down, Right down-then-up ---
  frameAUrl = renderFrameToDataUrl((ctx) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Head: Open Magenta Ring
    ctx.strokeStyle = "#ff2e88";
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(16, 7.5, 3.0, 0, Math.PI * 2);
    ctx.stroke();

    // Spine (Magenta)
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(16, 10.5);
    ctx.lineTo(16, 20);
    // Legs
    ctx.moveTo(16, 20);
    ctx.lineTo(11.5, 24.5);
    ctx.lineTo(9.5, 29);
    ctx.moveTo(16, 20);
    ctx.lineTo(20.5, 24.5);
    ctx.lineTo(22.5, 29);
    ctx.stroke();

    // Arms (Electric Cyan): Slanted diagonal crossing
    ctx.strokeStyle = "#00d3d9";
    ctx.lineWidth = 2.0;
    // Left arm: up-left elbow -> down-left hand
    ctx.beginPath();
    ctx.moveTo(16, 14);
    ctx.lineTo(9.5, 10.5);
    ctx.lineTo(7, 16.5);
    ctx.stroke();
    ctx.fillStyle = "#00d3d9";
    ctx.beginPath();
    ctx.arc(7, 16.5, 1.4, 0, Math.PI * 2);
    ctx.fill();

    // Right arm: down-right elbow -> up-right hand
    ctx.beginPath();
    ctx.moveTo(16, 14);
    ctx.lineTo(22.5, 17.5);
    ctx.lineTo(25, 11.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(25, 11.5, 1.4, 0, Math.PI * 2);
    ctx.fill();
  });

  // --- Frame B (Homepage Frame 2): Reversed arms (Left down-then-up, Right up-then-down) ---
  frameBUrl = renderFrameToDataUrl((ctx) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Head: Open Magenta Ring
    ctx.strokeStyle = "#ff2e88";
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(16, 7.5, 3.0, 0, Math.PI * 2);
    ctx.stroke();

    // Spine (Magenta)
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(16, 10.5);
    ctx.lineTo(16, 20);
    // Legs
    ctx.moveTo(16, 20);
    ctx.lineTo(11.5, 24.5);
    ctx.lineTo(9.5, 29);
    ctx.moveTo(16, 20);
    ctx.lineTo(20.5, 24.5);
    ctx.lineTo(22.5, 29);
    ctx.stroke();

    // Arms (Electric Cyan): Reversed diagonal crossing
    ctx.strokeStyle = "#00d3d9";
    ctx.lineWidth = 2.0;
    // Left arm: down-left elbow -> up-left hand
    ctx.beginPath();
    ctx.moveTo(16, 14);
    ctx.lineTo(9.5, 17.5);
    ctx.lineTo(7, 11.5);
    ctx.stroke();
    ctx.fillStyle = "#00d3d9";
    ctx.beginPath();
    ctx.arc(7, 11.5, 1.4, 0, Math.PI * 2);
    ctx.fill();

    // Right arm: up-right elbow -> down-right hand
    ctx.beginPath();
    ctx.moveTo(16, 14);
    ctx.lineTo(22.5, 10.5);
    ctx.lineTo(25, 16.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(25, 16.5, 1.4, 0, Math.PI * 2);
    ctx.fill();
  });

  // --- Room Active (Concept B): Static Frame A with Cybernetic Reticle Brackets ---
  roomActiveUrl = renderFrameToDataUrl((ctx) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Cyan HUD Corner Brackets
    ctx.strokeStyle = "#00d3d9";
    ctx.lineWidth = 1.6;
    // Top-Left [
    ctx.beginPath(); ctx.moveTo(4, 9); ctx.lineTo(4, 4); ctx.lineTo(9, 4); ctx.stroke();
    // Top-Right ]
    ctx.beginPath(); ctx.moveTo(23, 4); ctx.lineTo(28, 4); ctx.lineTo(28, 9); ctx.stroke();
    // Bottom-Left [
    ctx.beginPath(); ctx.moveTo(4, 23); ctx.lineTo(4, 28); ctx.lineTo(9, 28); ctx.stroke();
    // Bottom-Right ]
    ctx.beginPath(); ctx.moveTo(23, 28); ctx.lineTo(28, 28); ctx.lineTo(28, 23); ctx.stroke();

    // Head: Open Magenta Ring
    ctx.strokeStyle = "#ff2e88";
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(16, 7.5, 3.0, 0, Math.PI * 2);
    ctx.stroke();

    // Spine & Legs
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(16, 10.5);
    ctx.lineTo(16, 20);
    ctx.moveTo(16, 20); ctx.lineTo(11.5, 24.5); ctx.lineTo(9.5, 29);
    ctx.moveTo(16, 20); ctx.lineTo(20.5, 24.5); ctx.lineTo(22.5, 29);
    ctx.stroke();

    // Arms: Frame A pose
    ctx.strokeStyle = "#00d3d9";
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.moveTo(16, 14); ctx.lineTo(9.5, 10.5); ctx.lineTo(7, 16.5);
    ctx.moveTo(16, 14); ctx.lineTo(22.5, 17.5); ctx.lineTo(25, 11.5);
    ctx.stroke();

    // Joints
    ctx.fillStyle = "#00d3d9";
    ctx.beginPath(); ctx.arc(7, 16.5, 1.4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(25, 11.5, 1.4, 0, Math.PI * 2); ctx.fill();
  });
}

function updateFavicon(url) {
  if (!url) return;
  const link = getFaviconLink();
  if (link && link.href !== url) {
    link.href = url;
  }
}

/**
 * Starts the dynamic 2-frame dancing favicon on the Homepage.
 * Automatically throttled when page is hidden to conserve energy.
 */
export function startHomepageFavicon() {
  initCachedFrames();
  currentMode = "homepage";

  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  let isFrameA = true;
  updateFavicon(frameAUrl);

  timer = setInterval(() => {
    // Suspend updates if document is hidden (background tab)
    if (document.hidden || currentMode !== "homepage") return;
    isFrameA = !isFrameA;
    updateFavicon(isFrameA ? frameAUrl : frameBUrl);
  }, 440); // ~136 BPM disco groove tempo
}

/**
 * Snaps favicon to static Concept B (Tracked Stick Figure with HUD brackets)
 * Stops any active timer: 0% CPU, 0% GC during WebGL 3D execution.
 */
export function setRoomActiveFavicon() {
  initCachedFrames();
  currentMode = "room";

  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  updateFavicon(roomActiveUrl);
}

/**
 * Complete teardown/cleanup
 */
export function stopFavicon() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  currentMode = null;
}
